import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_MS = 24 * 60 * 60 * 1000;
const keyFor = (p) => `m4l_popup_${p.id}_${p.updated_at || ''}`;

// Frequency rules: once (ever), daily, session (per browser tab session), every (each page visit)
const isSuppressed = (p) => {
    const k = keyFor(p);
    if (p.frequency === 'every') return false;
    if (p.frequency === 'session') return !!sessionStorage.getItem(k);
    const last = Number(localStorage.getItem(k) || 0);
    if (!last) return false;
    return p.frequency === 'once' ? true : Date.now() - last < DAY_MS;
};

const markShown = (p) => {
    const k = keyFor(p);
    if (p.frequency === 'session') sessionStorage.setItem(k, '1');
    else if (p.frequency !== 'every') localStorage.setItem(k, String(Date.now()));
};

// Anonymous per-browser id so admins can see unique people, not just totals
const visitorId = () => {
    let id = localStorage.getItem('m4l_visitor_id');
    if (!id) {
        id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
        localStorage.setItem('m4l_visitor_id', id);
    }
    return id;
};

const track = (p, event) => {
    axios.post(`${API_URL}/api/popups/${p.id}/track`, { event, visitor_id: visitorId() }).catch(() => {});
};

export default function SitePopup() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { loading } = useAuth();
    const [popup, setPopup] = useState(null);

    useEffect(() => {
        setPopup(null);
        if (loading || pathname.startsWith('/admin')) return undefined;
        let timer;
        let cancelled = false;
        axios.get(`${API_URL}/api/popups/active`, { params: { path: pathname }, withCredentials: true })
            .then(({ data }) => {
                const next = (data || []).find(p => !isSuppressed(p));
                if (!next || cancelled) return;
                timer = setTimeout(() => {
                    if (cancelled) return;
                    markShown(next);
                    track(next, 'view');
                    setPopup(next);
                }, (next.delay_seconds || 0) * 1000);
            })
            .catch(() => {});
        return () => { cancelled = true; clearTimeout(timer); };
    }, [pathname, loading]);

    if (!popup) return null;

    const close = () => { track(popup, 'close'); setPopup(null); };
    const go = () => {
        const url = (popup.link_url || '').trim();
        if (!url) return;
        track(popup, 'click');
        setPopup(null);
        if (url.startsWith('/')) navigate(url);
        else window.open(url, '_blank', 'noopener,noreferrer');
    };
    const hasLink = !!(popup.link_url || '').trim();

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#1A1A2E]/60 backdrop-blur-sm animate-fade-in" onClick={close} data-testid="site-popup-overlay">
            <div
                className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-[#F9F6F0] rounded-sm shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="site-popup"
            >
                <button onClick={close} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 text-[#1A1A2E] flex items-center justify-center hover:bg-white" aria-label="Close" data-testid="site-popup-close">
                    <X className="w-4 h-4" />
                </button>
                {popup.image_url && (
                    <img
                        src={resolveImageUrl(popup.image_url)}
                        alt={popup.title || ''}
                        onClick={go}
                        className={`w-full max-h-[50vh] object-cover ${hasLink ? 'cursor-pointer' : ''}`}
                        data-testid="site-popup-image"
                    />
                )}
                {(popup.title || popup.body || hasLink) && (
                    <div className="p-6 sm:p-8 text-center">
                        {popup.title && <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1A2E] mb-3" data-testid="site-popup-title">{popup.title}</h2>}
                        {popup.body && <p className="font-sans text-sm sm:text-base text-[#4A4A68] whitespace-pre-line mb-6" data-testid="site-popup-body">{popup.body}</p>}
                        {hasLink && (
                            <button onClick={go} className="btn-primary w-full" data-testid="site-popup-cta">
                                {popup.button_label || 'Discover'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
